export class Binding {
  matches(_proto: unknown): boolean {
    throw Error("Binding.matches not implemented");
  }

  create(defaultFactory: () => unknown) {
    return defaultFactory();
  }
}

export class ClassBinding<
  Class extends
    | {
        __honk_clsid?: string;
        scope?: string;
      }
    | null
    | undefined = undefined,
> extends Binding {
  public iface: Class;
  public impl?: { new (...args: any[]): unknown };
  public scope?: string;

  constructor(iface: Class) {
    super();
    this.iface = iface;
    if (this.iface == null) {
      throw Error("Interface is undefined");
    }
    if (this.iface.scope != null) {
      this.inScope(this.iface.scope);
    }
  }

  to(impl: { new (...args: any[]): unknown; scope?: string }) {
    if (impl == null) {
      throw Error("Implementation is undefined");
    }
    if (this.impl != null) {
      throw Error("Already bound");
    }
    this.impl = impl;
    if (impl.scope != null) {
      this.scope = impl.scope.toUpperCase();
    }
    return this;
  }

  inScope(scope: string) {
    const formattedScope = scope.toUpperCase();
    if (this.scope != null && this.scope !== scope) {
      throw Error(`Already scoped to ${this.scope}`);
    }
    if (formattedScope === "SINGLETON") {
      this.scope = formattedScope;
    } else if (formattedScope !== "INSTANCE") {
      throw Error(`Invalid scope, '${scope}'`);
    }
    return this;
  }

  matches(cls: object) {
    return cls === this.iface || this._clsIdMatches(cls);
  }

  _clsIdMatches({ __honk_clsid: id }: { __honk_clsid?: string }) {
    return id !== undefined && id === this.iface?.__honk_clsid;
  }
}

class ConstantBinding extends Binding {
  public name: unknown;
  public value: unknown;

  constructor(name: unknown) {
    super();
    this.name = name;
  }

  to(value: unknown) {
    this.value = value;
  }

  matches(cls: unknown) {
    return cls === this.name;
  }

  create() {
    return this.value;
  }
}

interface Binder {
  new (): Binder;
  bindings: Binding[];
  bind<Iface extends object>(iface: Iface): ClassBinding<Iface>;
  bindConstant(name: unknown): ConstantBinding;
}

const Binder = function (this: Binder) {
  this.bindings = [];

  this.bind = <Iface extends object>(iface: Iface) => {
    return bind(new ClassBinding(iface));
  };

  this.bindConstant = (name: unknown) => {
    return bind(new ConstantBinding(name));
  };

  const bind = <BindingType extends Binding>(binding: BindingType) => {
    this.bindings = [binding, ...this.bindings];
    return binding;
  };
} as unknown as Binder;

export default Binder;
